// packages/web-ui/src/components/file-tree/InlineRename.tsx
//
// Story W3-S5 AC #5 — inline rename input component.
//
// The row's name span swaps for an `<input>` pre-filled with the current
// name; basename auto-selected (extension excluded). Enter commits, Esc
// cancels. Client-side validation per AC #5.3:
//   - Empty name           → inline error "Name cannot be empty"
//   - Same as current name → silent cancel
//   - Contains `/` or `\`  → "Name cannot contain path separators"
//   - Contains `..`        → "Name cannot contain '..'"
//
// The component is purely UI — actual `filesApi.rename` dispatch is the
// caller's concern. The caller passes `onCommit(newName)` which returns a
// `Promise<{ ok: true } | { ok: false, message: string }>`. While the
// promise is pending the input is disabled; on `ok:false` the message
// stays inline; on `ok:true` the consumer unmounts this component.

import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

export interface InlineRenameProps {
  /** Current basename (no path, no leading dir). */
  initialName: string;
  /** Commit handler — returns Ok or an inline error message. */
  onCommit: (newName: string) => Promise<{ ok: true } | { ok: false; message: string }>;
  /** Cancel handler — Esc, blur-without-Enter, or unchanged-input commit. */
  onCancel: () => void;
}

/**
 * Validate a candidate name. Returns `null` on success or the inline error
 * message string. Pure — exported for tests.
 */
export function validateRename(name: string): string | null {
  if (name.length === 0) return 'Name cannot be empty';
  if (name.includes('/') || name.includes('\\')) {
    return 'Name cannot contain path separators';
  }
  if (name.includes('..')) {
    return "Name cannot contain '..'";
  }
  return null;
}

/**
 * Compute the basename selection range. Selects everything before the LAST
 * `.` so users replace the human-readable part. For dotfiles (no `.` after
 * position 0), selects the whole name.
 */
export function basenameSelectionRange(name: string): [number, number] {
  const lastDot = name.lastIndexOf('.');
  if (lastDot <= 0) return [0, name.length];
  return [0, lastDot];
}

export function InlineRename(props: InlineRenameProps): JSX.Element {
  const { initialName, onCommit, onCancel } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const el = inputRef.current;
    if (el === null) return;
    el.focus();
    const [start, end] = basenameSelectionRange(initialName);
    el.setSelectionRange(start, end);
  }, [initialName]);

  const commit = async (): Promise<void> => {
    if (pending) return;
    if (value === initialName) {
      onCancel();
      return;
    }
    const validationError = validateRename(value);
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    setPending(true);
    setError(null);
    const res = await onCommit(value);
    setPending(false);
    if (!res.ok) {
      setError(res.message);
    }
    // success → consumer unmounts us
  };

  return (
    <span data-testid="inline-rename">
      <input
        ref={inputRef}
        type="text"
        data-testid="inline-rename-input"
        value={value}
        disabled={pending}
        aria-invalid={error !== null}
        aria-errormessage={error !== null ? 'inline-rename-error' : undefined}
        onChange={(e) => {
          setValue(e.target.value);
          if (error !== null) setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        className="h-6 w-full rounded border border-taupe-500 bg-cream-50 px-1 text-xs"
      />
      {error !== null ? (
        <span
          id="inline-rename-error"
          data-testid="inline-rename-error"
          role="alert"
          className="block text-[10px] text-red-700"
        >
          {error}
        </span>
      ) : null}
    </span>
  );
}
